import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DatasetSelectorModal from "src/reusable/Chat/PromptInput/DatasetSelectorModal";
import type { Dataset as ArtifactSelectorDataset } from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";
import WorkflowPersonaSlotsPanel from "src/views/products/workflow/components/WorkflowPersonaSlotsPanel";
import InputDocumentsUploader from "src/views/products/workflow/components/InputDocumentsUploader";
import { useItemEquivalencyRunStore } from "src/views/products/item-equivalency/store/ItemEquivalencyRunStore";
import { partitionBlockingInputFiles } from "src/views/products/workflow/store/inputFileReadiness";

import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { NucToast } from "src/reusable/components/Toast";
import { useWorkflowLeadPersona } from "src/views/products/workflow/hooks/useWorkflowLeadPersona";
import { useStartItemEquivalency } from "src/views/products/item-equivalency/services/ItemEquivalencyService";
import {
  ITEM_EQUIVALENCY_PRODUCT,
  itemEquivalencyChatUrl,
} from "src/views/products/item-equivalency/itemEquivalencyProduct";

import PartsAISplashScreen from "./components/PartsAISplashScreen";
import WorkflowSplashChatInput from "src/views/products/workflow/components/WorkflowSplashChatInput";
import WorkflowInputFilesViewer from "src/views/products/workflow/components/WorkflowInputFilesViewer";
import PartPickerDialog from "./components/PartPickerDialog";
import PartCard from "./dashboard/PartCard";
import { usePartsAIStore } from "./store/PartsAIStore";
import { useGetPart, type PartListItem } from "./services/PartService";
import { PARTS_AI_COMPARISON_LISTING_PRODUCT_KEY } from "./utils/chatUrl";

// First-turn trigger only: the injected record context already carries the
// item pair and gate protocol, and restating them in editable prose created
// a second authority that could contradict the record.
const COMPARISON_INITIAL_MESSAGE = "Begin the item equivalency evaluation.";

// Module-level so the consumer's effect dependency arrays don't churn.
const NOOP_TERMINATE_CONNECTION = () => {};

interface PartsAIComparisonSplashLocationState {
  // Pre-select the two parts when navigated here with two parts in hand.
  partAId?: number;
  partBId?: number;
}

const PartsAIComparisonSplashPage = () => {
  const { isLoading: isLoadingPersona } = useWorkflowLeadPersona(
    ITEM_EQUIVALENCY_PRODUCT,
  );

  const {
    config: chatConfig,
    setActiveDialogueId,
    setInitialUserMessage,
    clearUploadedFiles,
    setProductConfig,
    registerChatUrlBuilder,
    isChatReady,
    dropdownDatasets,
    setDropdownDatasets,
  } = useChatStore();

  // Reference datasets scope the evaluation's Search tool (PG parity):
  // picks land in ChatStore and ride every converse turn of the dialogue.
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const handleDatasetsConfirm = useCallback(
    (datasets: ArtifactSelectorDataset[]) => {
      setDropdownDatasets(datasets);
    },
    [setDropdownDatasets],
  );

  const { reset } = usePartsAIStore();
  const history = useHistory();
  const location = useLocation<PartsAIComparisonSplashLocationState>();

  // Pre-selected parts (by id) are fetched for display; a user pick overrides.
  const { data: fetchedA } = useGetPart(location.state?.partAId);
  const { data: fetchedB } = useGetPart(location.state?.partBId);
  const [pickedA, setPickedA] = useState<PartListItem | null>(null);
  const [pickedB, setPickedB] = useState<PartListItem | null>(null);
  const partA: PartListItem | null = pickedA ?? fetchedA ?? null;
  const partB: PartListItem | null = pickedB ?? fetchedB ?? null;
  const [picking, setPicking] = useState<null | "A" | "B">(null);

  // Latch fetched pre-selections into component state so a later
  // location.state change (e.g. the uploader recording its pre-created
  // dialogue id) cannot clear the chosen parts out from under the user.
  useEffect(() => {
    if (fetchedA) setPickedA((current) => current ?? fetchedA);
  }, [fetchedA]);
  useEffect(() => {
    if (fetchedB) setPickedB((current) => current ?? fetchedB);
  }, [fetchedB]);

  const { mutateAsync: startEvaluation, isLoading: isCreating } =
    useStartItemEquivalency();

  const handleSubmit = async (userInput: string): Promise<boolean> => {
    if (isCreating) {
      return false;
    }
    if (!partA || !partB) {
      NucToast({
        message: "Select two parts to compare first.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
    if (partA.id === partB.id) {
      // The pickers exclude each other, so this only catches a prefilled
      // pair (deep link, or a part latched into both slots). The API rejects
      // it too; this keeps the message ours instead of a 422.
      NucToast({
        message:
          "Pick two different parts — a part cannot be compared to itself.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
    if (!isChatReady()) {
      NucToast({
        message: "Chat is not ready. Please wait for configuration to load.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    const runState = useItemEquivalencyRunStore.getState();
    const { failed, uploading } = partitionBlockingInputFiles(
      runState.inputFiles,
    );
    if (failed.length > 0) {
      NucToast({
        message: `Upload failed for ${failed
          .map((file) => file.name)
          .join(", ")}. Remove the failed document(s) to continue.`,
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
    if (uploading.length > 0) {
      NucToast({
        message: "Documents are still uploading. Please wait a moment.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    try {
      // Send the selection with the request that creates the evaluation:
      // that is the first moment the server can store it.
      const sourceDataRecordIds = [
        ...runState.inputFiles
          .map((file) => file.sdrId)
          .filter((id): id is number => Boolean(id)),
        ...runState.selectedArtifacts
          .map((artifact) => artifact.sdr_id)
          .filter((id): id is number => Boolean(id)),
      ];
      const evaluation = await startEvaluation({
        originalPartId: partA.id,
        replacementPartId: partB.id,
        chatConfigId: chatConfig?.chatConfigId ?? null,
        // The uploader pre-creates the dialogue when documents are staged;
        // attaching the record to it keeps the documents on the evaluation.
        dialogueId: runState.dialogueId ?? null,
        sourceDataRecordIds,
        datasetIds: dropdownDatasets.map((dataset) => dataset.id),
      });
      if (sourceDataRecordIds.length > 0) {
        useChatStore.getState().setSourceDataRecordIds(sourceDataRecordIds);
      }
      setInitialUserMessage(userInput);
      history.push(itemEquivalencyChatUrl(evaluation.dialogue_id));
      return true;
    } catch (error: any) {
      console.error(
        "[PartsAIComparisonSplashPage] Failed to create comparison:",
        error,
      );
      NucToast({
        message:
          error?.response?.data?.detail ||
          "Failed to start comparison. Please try again.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
  };

  useEffect(() => {
    // Reset chat state on mount so we don't render stale data.
    setActiveDialogueId(null);
    setInitialUserMessage(undefined);
    clearUploadedFiles();
    useChatStore.getState().setDropdownDatasets([]);
    useChatStore.getState().setSourceDataRecordIds([]);
    useItemEquivalencyRunStore.getState().reset();
    reset();
    setProductConfig({
      productKey: ITEM_EQUIVALENCY_PRODUCT.productKey,
      productName: ITEM_EQUIVALENCY_PRODUCT.displayName,
      basePath: ITEM_EQUIVALENCY_PRODUCT.basePath,
    });
    // Registered under the same composite key the recents strip queries with.
    registerChatUrlBuilder(
      PARTS_AI_COMPARISON_LISTING_PRODUCT_KEY,
      async (dialogueId: number) => itemEquivalencyChatUrl(dialogueId),
    );
  }, [
    setActiveDialogueId,
    setInitialUserMessage,
    clearUploadedFiles,
    reset,
    setProductConfig,
    registerChatUrlBuilder,
  ]);

  if (isLoadingPersona) {
    return null;
  }

  const renderSlot = (
    label: string,
    sublabel: string,
    part: PartListItem | null,
    slot: "A" | "B",
  ) => (
    <Box
      sx={{ flex: 1, minWidth: 0 }}
      data-testid={`parts-ai-comparison-slot-${slot.toLowerCase()}`}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {sublabel}
        </Typography>
      </Stack>
      {part ? (
        <PartCard part={part} onClick={() => setPicking(slot)} />
      ) : (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setPicking(slot)}
          data-testid={`parts-ai-comparison-pick-${slot.toLowerCase()}`}
        >
          Select the {label.toLowerCase()} part
        </Button>
      )}
    </Box>
  );

  // Swapping reverses the record semantics (which part carries the burden of
  // proof), so it is a first-class action on the arrows between the slots —
  // cheaper than re-picking both parts when they were chosen in reverse.
  const handleSwap = () => {
    const [a, b] = [partA, partB];
    setPickedA(b);
    setPickedB(a);
  };

  const headerContent = (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ width: "100%" }}
        data-testid="parts-ai-comparison-picker-row"
      >
        {/* Labeled by ROLE, not position: the record semantics are
            original/replacement, and "Part A / Part B" invited picking them
            in reverse — which flips the burden of proof and produces
            legitimate-but-unanswerable missing-evidence asks. */}
        {renderSlot("Installed", "original", partA, "A")}
        <Tooltip title="Swap installed and candidate">
          <span>
            <IconButton
              onClick={handleSwap}
              disabled={!partA && !partB}
              data-testid="parts-ai-comparison-swap"
            >
              <CompareArrowsIcon />
            </IconButton>
          </span>
        </Tooltip>
        {renderSlot("Candidate", "replacement", partB, "B")}
      </Stack>
      {/* One box: datasets to search + documents to extract. Split cards
          confused customers — both are "the material the evaluation works
          from", differing only in how it arrives. */}
      <Card elevation={2} sx={{ width: "100%", mt: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6" component="h2">
              Reference Datasets &amp; Documents
            </Typography>
            <Tooltip title="Selected datasets are searched for reference material (design basis, qualification records, prior evaluations) during the evaluation. Uploaded documents are the items' own source documents — datasheets, test reports, specifications — and the extract stage reads each one directly.">
              <IconButton
                size="small"
                data-testid="parts-ai-comparison-reference-info"
                aria-label="Reference material info"
                sx={{ p: 0.25 }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick datasets to search as reference material during the evaluation.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={<FolderIcon />}
              onClick={() => setIsDatasetModalOpen(true)}
              sx={{ textTransform: "none" }}
              data-testid="peq-btn-select-datasets"
            >
              Select Dataset{dropdownDatasets.length === 1 ? "" : "s"}
            </Button>
            {dropdownDatasets.map((dataset) => (
              <Chip
                key={dataset.id}
                label={dataset.name}
                size="small"
                onDelete={() =>
                  setDropdownDatasets(
                    dropdownDatasets.filter((d) => d.id !== dataset.id),
                  )
                }
                data-testid={`peq-dataset-chip-${dataset.id}`}
              />
            ))}
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload the items&apos; own source documents (datasheets, test
            reports, specifications) — the extract stage reads each one.
          </Typography>
          <InputDocumentsUploader
            store={useItemEquivalencyRunStore}
            productKey={ITEM_EQUIVALENCY_PRODUCT.productKey}
            testIdPrefix={ITEM_EQUIVALENCY_PRODUCT.testIdPrefix}
            analyticsPrefix="item-equivalency"
            uploadTooltip="Upload documents such as manufacturer datasheets, qualification test reports, or specifications for the original and replacement items."
          />
          <Box sx={{ mt: 2 }} data-testid="peq-staged-files">
            <WorkflowInputFilesViewer store={useItemEquivalencyRunStore} />
          </Box>
        </CardContent>
      </Card>
      <DatasetSelectorModal
        open={isDatasetModalOpen}
        onClose={() => setIsDatasetModalOpen(false)}
        onConfirm={handleDatasetsConfirm}
        defaultSelectedDatasets={dropdownDatasets}
      />
      {picking !== null && (
        <PartPickerDialog
          open
          title={
            picking === "A"
              ? "Select the installed (original) part"
              : "Select the candidate (replacement) part"
          }
          excludePartId={picking === "A" ? partB?.id : partA?.id}
          onClose={() => setPicking(null)}
          onSelect={(part) => {
            if (picking === "A") setPickedA(part);
            else setPickedB(part);
          }}
        />
      )}
    </>
  );

  return (
    <PartsAISplashScreen
      chatStreamError=""
      isChatConnectionError={false}
      isChatLoading={isCreating}
      isChatEnded={false}
      isTurnActive={false}
      terminateConnection={NOOP_TERMINATE_CONNECTION}
      sendUserMessage={handleSubmit}
      recentsProductKey={PARTS_AI_COMPARISON_LISTING_PRODUCT_KEY}
      recentsKind="comparison"
      // Recents live at the shared "/parts/recents", not this splash's own basePath.
      recentsBasePath="/parts"
      // The workflow composer, not PartsAIChatInput: uploads tag this product
      // instead of parts-ai, and the native attach path (which creates its
      // own dialogue outside the kickoff and overwrites location state) is
      // disabled — same kickoff surface as the 50.59 and PG splashes.
      chatInput={
        <WorkflowSplashChatInput
          defaultValue={COMPARISON_INITIAL_MESSAGE}
          sendUserMessage={handleSubmit}
          isChatLoading={isCreating}
          placeholder="Add application context for this equivalency evaluation…"
          product={ITEM_EQUIVALENCY_PRODUCT.productKey}
        />
      }
      headerContent={headerContent}
      topActionsLeft={
        <Button
          // href + onClick, not component={Link}: MUI's polymorphism does
          // not typecheck against the nested @types/react that
          // @types/react-router pulls in. Matches the 50.59 and PG pages.
          href="/parts/compare/runs"
          onClick={(event) => {
            event.preventDefault();
            history.push("/parts/compare/runs");
          }}
          size="small"
          startIcon={<ListAltOutlinedIcon />}
          sx={{ textTransform: "none" }}
          data-testid="peq-btn-runs"
        >
          Runs
        </Button>
      }
      topActions={
        <WorkflowPersonaSlotsPanel product={ITEM_EQUIVALENCY_PRODUCT} />
      }
    />
  );
};

export default PartsAIComparisonSplashPage;
